import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    ChevronLeft, Home, LogOut, ClipboardList, Plus, Save,
    Send, CheckCircle2, AlertCircle, FileText, Users,
    Download, Calendar, Eye, EyeOff, Clock, Star, ChevronDown
} from 'lucide-react';
import API_URL from '../config';
import LoadingScreen from '../components/LoadingScreen';
import './Home.css';
import './Profile.css';

// ─── Attendance Toggle ──────────────────────────────────────
const AttendanceBtn = ({ status, onChange, readOnly }) => {
    const opts = ['present', 'absent'];
    return (
        <div style={{ display: 'flex', gap: 4 }}>
            {opts.map(opt => (
                <button key={opt}
                    onClick={() => !readOnly && onChange(opt)}
                    style={{
                        padding: '3px 10px', borderRadius: '20px', fontSize: '0.72rem', fontWeight: 700,
                        border: 'none', cursor: readOnly ? 'default' : 'pointer', transition: 'all 0.2s',
                        background: status === opt
                            ? opt === 'present' ? '#22c55e' : '#ef4444'
                            : 'rgba(255,255,255,0.07)',
                        color: status === opt ? '#fff' : 'var(--text-secondary)',
                        opacity: status && status !== opt ? 0.4 : 1
                    }}>
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                </button>
            ))}
        </div>
    );
};

// ─── All Reviewers Summary (Coordinator view) ────────────────
const AllReviewsSummary = ({ allReviews, teamMembers }) => {
    if (!allReviews || allReviews.length === 0) return null;

    return (
        <div style={{
            marginBottom: '1.6rem',
            background: 'rgba(99,102,241,0.05)',
            border: '1px solid rgba(99,102,241,0.2)',
            borderRadius: '14px',
            overflow: 'hidden'
        }}>
            <div style={{
                padding: '10px 14px',
                borderBottom: '1px solid rgba(99,102,241,0.15)',
                fontSize: '0.75rem', fontWeight: 700, color: '#818cf8',
                textTransform: 'uppercase', letterSpacing: '0.5px',
                display: 'flex', alignItems: 'center', gap: 6
            }}>
                <ClipboardList size={13} />
                All Reviewer Submissions ({allReviews.length})
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {allReviews.map((rev, idx) => {
                    let attMap = {};
                    try { attMap = JSON.parse(rev.attendance_data || '{}'); } catch (e) {}
                    const presentCount = Object.values(attMap).filter(v => v === 'present').length;
                    const totalCount = teamMembers?.length || 0;

                    return (
                        <div key={rev.reviewer_email} style={{
                            padding: '12px 14px',
                            borderBottom: idx < allReviews.length - 1 ? '1px solid rgba(99,102,241,0.1)' : 'none',
                        }}>
                            {/* Reviewer identity row */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <div style={{
                                        width: 28, height: 28, borderRadius: '8px', flexShrink: 0,
                                        background: `hsl(${(idx * 67 + 220) % 360},70%,55%)`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontWeight: 800, color: '#fff', fontSize: '0.75rem'
                                    }}>
                                        {(rev.reviewer_name || rev.reviewer_email || '?').charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>
                                            {rev.reviewer_name || rev.reviewer_email}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                    {/* Attendance pill */}
                                    {Object.keys(attMap).length > 0 && (
                                        <span style={{
                                            fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: '20px',
                                            background: presentCount === totalCount ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.12)',
                                            color: presentCount === totalCount ? '#22c55e' : '#ef4444'
                                        }}>
                                            {presentCount}/{totalCount} present
                                        </span>
                                    )}
                                    {/* Marks pill */}
                                    {rev.marks != null && (
                                        <span style={{
                                            fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: '20px',
                                            background: 'rgba(99,102,241,0.15)', color: '#818cf8',
                                            display: 'flex', alignItems: 'center', gap: 3
                                        }}>
                                            <Star size={10} /> {rev.marks} marks
                                        </span>
                                    )}
                                    {/* Sent pill */}
                                    {rev.feedback_sent && (
                                        <span style={{
                                            fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: '20px',
                                            background: 'rgba(34,197,94,0.15)', color: '#22c55e',
                                            display: 'flex', alignItems: 'center', gap: 3
                                        }}>
                                            <CheckCircle2 size={10} /> Sent
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Per-student attendance breakdown */}
                            {Object.keys(attMap).length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                                    {(teamMembers || []).map(m => {
                                        const status = attMap[m.email];
                                        return (
                                            <span key={m.email} style={{
                                                fontSize: '0.68rem', padding: '2px 8px', borderRadius: '20px',
                                                background: status === 'present'
                                                    ? 'rgba(34,197,94,0.12)' : status === 'absent'
                                                        ? 'rgba(239,68,68,0.12)' : 'rgba(255,255,255,0.06)',
                                                color: status === 'present' ? '#22c55e' : status === 'absent' ? '#ef4444' : 'var(--text-secondary)',
                                                border: `1px solid ${status === 'present' ? 'rgba(34,197,94,0.2)' : status === 'absent' ? 'rgba(239,68,68,0.2)' : 'var(--glass-border)'}`
                                            }}>
                                                {m.name?.split(' ')[0] || m.rollNo} — {status || 'N/A'}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Private feedback (coordinator only) */}
                            {rev.private_feedback && (
                                <div style={{
                                    background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.18)',
                                    borderRadius: '8px', padding: '8px 12px', marginBottom: 6, fontSize: '0.82rem'
                                }}>
                                    <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#f59e0b', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <EyeOff size={10} /> Internal Note
                                    </div>
                                    <div style={{ whiteSpace: 'pre-wrap', opacity: 0.85 }}>{rev.private_feedback}</div>
                                </div>
                            )}

                            {/* Student feedback */}
                            {rev.student_feedback && (
                                <div style={{
                                    background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.15)',
                                    borderRadius: '8px', padding: '8px 12px', fontSize: '0.82rem'
                                }}>
                                    <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#22c55e', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                                        <Eye size={10} /> Student Feedback
                                    </div>
                                    <div style={{ whiteSpace: 'pre-wrap', opacity: 0.85 }}>{rev.student_feedback}</div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ─── Team Review Card ───────────────────────────────────────
const TeamCard = ({ team, task, sessionId, reviewerEmail, myReview, allReviews, onSaved, isCoordinator }) => {
    const [attendance, setAttendance] = useState({});
    const [marks, setMarks] = useState('');
    const [privateFeedback, setPrivateFeedback] = useState('');
    const [studentFeedback, setStudentFeedback] = useState('');
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);
    const [saved, setSaved] = useState(false);
    const [expanded, setExpanded] = useState(false);

    // Pre-fill with THIS reviewer's own previous submission
    useEffect(() => {
        if (myReview) {
            setMarks(myReview.marks != null ? String(myReview.marks) : '');
            setPrivateFeedback(myReview.private_feedback || '');
            setStudentFeedback(myReview.student_feedback || '');
            try {
                const att = JSON.parse(myReview.attendance_data || '{}');
                setAttendance(att);
            } catch (e) {}
        }
    }, [myReview]);

    const submit = async (sendFeedback = false) => {
        if (sendFeedback) setSending(true);
        else setSaving(true);
        try {
            const res = await fetch(`${API_URL}/formation/tasks/${task.id}/review-sessions/${sessionId}/submit`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    reviewer_email: reviewerEmail,
                    team_id: team.id,
                    attendance_data: attendance,
                    marks: marks !== '' ? parseFloat(marks) : null,
                    private_feedback: privateFeedback,
                    student_feedback: studentFeedback,
                    send_feedback: sendFeedback
                })
            });
            if (res.ok) {
                setSaved(true);
                setTimeout(() => setSaved(false), 2500);
                onSaved && onSaved();
            } else {
                const e = await res.json();
                alert(e.error || 'Failed to save');
            }
        } catch (err) {
            alert('Error saving review');
        } finally {
            setSaving(false);
            setSending(false);
        }
    };

    const presentCount = Object.values(attendance).filter(v => v === 'present').length;
    const totalCount = team.members?.length || 0;
    const otherReviews = (allReviews || []).filter(r =>
        r.reviewer_email?.toLowerCase() !== reviewerEmail?.toLowerCase()
    );
    const submittedCount = allReviews?.length || 0;

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
                background: 'var(--glass-bg)',
                border: `1px solid ${myReview?.feedback_sent ? 'rgba(34,197,94,0.3)' : 'var(--glass-border)'}`,
                borderRadius: '20px',
                overflow: 'hidden',
                transition: 'all 0.3s'
            }}
        >
            {/* Card Header */}
            <div
                onClick={() => setExpanded(e => !e)}
                style={{
                    padding: '1.1rem 1.4rem', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '1rem',
                    borderBottom: expanded ? '1px solid var(--glass-border)' : 'none'
                }}
            >
                {/* Team Number Badge */}
                <div style={{
                    width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
                    background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, color: '#fff', fontSize: '1rem'
                }}>
                    {team.team_number || team.id}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '2px' }}>{team.team_name}</div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.73rem', opacity: 0.6 }}>
                            <Users size={11} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                            {totalCount} members
                        </span>

                        {/* How many reviewers have submitted */}
                        {submittedCount > 0 && (
                            <span style={{
                                fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: '20px',
                                background: 'rgba(99,102,241,0.12)', color: '#818cf8',
                                display: 'flex', alignItems: 'center', gap: 3
                            }}>
                                <ClipboardList size={10} />
                                {submittedCount} review{submittedCount !== 1 ? 's' : ''} submitted
                            </span>
                        )}

                        {/* My attendance summary */}
                        {Object.keys(attendance).length > 0 && (
                            <span style={{
                                fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: '20px',
                                background: presentCount === totalCount ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.12)',
                                color: presentCount === totalCount ? '#22c55e' : '#ef4444'
                            }}>
                                {presentCount}/{totalCount} present
                            </span>
                        )}

                        {/* My marks */}
                        {marks !== '' && (
                            <span style={{
                                fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px',
                                borderRadius: '20px', background: 'rgba(99,102,241,0.15)', color: '#818cf8'
                            }}>
                                <Star size={10} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                                {marks} marks
                            </span>
                        )}

                        {myReview?.feedback_sent && (
                            <span style={{
                                fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px',
                                borderRadius: '20px', background: 'rgba(34,197,94,0.15)', color: '#22c55e',
                                display: 'flex', alignItems: 'center', gap: 3
                            }}>
                                <CheckCircle2 size={10} /> Sent
                            </span>
                        )}

                        {team.problem_statement && (
                            <span style={{ color: '#a78bfa', opacity: 0.8 }}>
                                <FileText size={14} style={{ verticalAlign: 'middle' }} />
                            </span>
                        )}
                    </div>
                </div>

                <ChevronDown size={18} style={{
                    opacity: 0.4, transition: 'transform 0.2s', flexShrink: 0,
                    transform: expanded ? 'rotate(180deg)' : 'rotate(0)'
                }} />
            </div>

            {/* Expanded Content */}
            <AnimatePresence>
                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        style={{ overflow: 'hidden' }}
                    >
                        <div style={{ padding: '1.4rem' }}>

                            {/* Problem Statement */}
                            {team.problem_statement && (() => {
                                let psList = [];
                                try {
                                    const p = JSON.parse(team.problem_statement);
                                    psList = Array.isArray(p) ? p : [p];
                                } catch (e) { psList = [team.problem_statement]; }
                                return (
                                    <div style={{
                                        background: 'rgba(167,139,250,0.07)', border: '1px solid rgba(167,139,250,0.2)',
                                        borderRadius: '12px', padding: '1rem 1.2rem', marginBottom: '1.4rem'
                                    }}>
                                        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#a78bfa', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                            <FileText size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Problem Statement(s)
                                        </div>
                                        {psList.filter(p => p && p.trim()).map((ps, idx) => (
                                            <div key={idx} style={{ fontSize: '0.88rem', opacity: 0.85, marginBottom: idx < psList.length - 1 ? '8px' : 0 }}>
                                                {psList.length > 1 && <span style={{ opacity: 0.4, marginRight: '6px' }}>{idx + 1}.</span>}
                                                {ps}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}

                            {/* ── Coordinator: show all other reviewers' submissions first ── */}
                            {isCoordinator && allReviews && allReviews.length > 0 && (
                                <AllReviewsSummary allReviews={allReviews} teamMembers={team.members} />
                            )}

                            {/* ── Divider for coordinator's own entry ── */}
                            {isCoordinator && (
                                <div style={{
                                    fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase',
                                    letterSpacing: '0.5px', opacity: 0.5, marginBottom: '1rem',
                                    paddingBottom: '0.5rem', borderBottom: '1px dashed var(--glass-border)'
                                }}>
                                    Your Evaluation
                                </div>
                            )}

                            {/* Attendance */}
                            <div style={{ marginBottom: '1.4rem' }}>
                                <div style={{ fontSize: '0.78rem', fontWeight: 700, opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
                                    Attendance
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {(team.members || []).map(m => (
                                        <div key={m.email} style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            padding: '8px 12px', borderRadius: '10px', transition: 'all 0.2s',
                                            background: attendance[m.email] === 'present'
                                                ? 'rgba(34,197,94,0.07)' : attendance[m.email] === 'absent'
                                                    ? 'rgba(239,68,68,0.07)' : 'rgba(255,255,255,0.04)',
                                            border: `1px solid ${attendance[m.email] === 'present'
                                                ? 'rgba(34,197,94,0.2)' : attendance[m.email] === 'absent'
                                                    ? 'rgba(239,68,68,0.2)' : 'var(--glass-border)'}`
                                        }}>
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{m.name || '(Unknown)'}</div>
                                                <div style={{ fontSize: '0.73rem', opacity: 0.5 }}>{m.rollNo || m.email}</div>
                                            </div>
                                            <AttendanceBtn
                                                status={attendance[m.email]}
                                                onChange={val => setAttendance(prev => ({ ...prev, [m.email]: val }))}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Marks */}
                            <div style={{ marginBottom: '1.4rem' }}>
                                <label style={{ fontSize: '0.78rem', fontWeight: 700, opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>
                                    <Star size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Marks / Score
                                </label>
                                <input
                                    type="number"
                                    className="profile-input"
                                    style={{ marginBottom: 0, maxWidth: '160px' }}
                                    placeholder="e.g. 85"
                                    value={marks}
                                    onChange={e => setMarks(e.target.value)}
                                    min={0} max={100} step={0.5}
                                />
                                <div style={{ fontSize: '0.72rem', opacity: 0.4, marginTop: '5px' }}>
                                    🔒 Marks are strictly internal — students will never see these
                                </div>
                            </div>

                            {/* Private Feedback */}
                            <div style={{ marginBottom: '1.4rem' }}>
                                <label style={{
                                    fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase',
                                    letterSpacing: '0.5px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px',
                                    color: '#f59e0b'
                                }}>
                                    <EyeOff size={13} /> Internal Comments
                                    <span style={{ fontWeight: 400, fontSize: '0.7rem', opacity: 0.6, textTransform: 'none', letterSpacing: 0 }}>
                                        (Coordinator/Admin only — NOT sent to students)
                                    </span>
                                </label>
                                <textarea
                                    className="profile-input"
                                    style={{
                                        marginBottom: 0, minHeight: '80px', resize: 'vertical',
                                        borderColor: 'rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.04)'
                                    }}
                                    placeholder="Private observations, concerns, coordinator-only notes..."
                                    value={privateFeedback}
                                    onChange={e => setPrivateFeedback(e.target.value)}
                                />
                            </div>

                            {/* Student Feedback */}
                            <div style={{ marginBottom: '1.4rem' }}>
                                <label style={{
                                    fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase',
                                    letterSpacing: '0.5px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px',
                                    color: '#22c55e'
                                }}>
                                    <Eye size={13} /> Student Feedback
                                    <span style={{ fontWeight: 400, fontSize: '0.7rem', opacity: 0.6, textTransform: 'none', letterSpacing: 0 }}>
                                        (Visible to team &amp; sent via notifications)
                                    </span>
                                </label>
                                <textarea
                                    className="profile-input"
                                    style={{
                                        marginBottom: 0, minHeight: '90px', resize: 'vertical',
                                        borderColor: 'rgba(34,197,94,0.3)', background: 'rgba(34,197,94,0.04)'
                                    }}
                                    placeholder="Write feedback for the team — they will receive this via email, push notification & in-app..."
                                    value={studentFeedback}
                                    onChange={e => setStudentFeedback(e.target.value)}
                                />
                            </div>

                            {/* Action Buttons */}
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                <motion.button
                                    whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                                    onClick={() => submit(false)}
                                    disabled={saving || sending}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '7px',
                                        padding: '9px 18px', borderRadius: '12px', border: '1px solid var(--glass-border)',
                                        background: saved ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.07)',
                                        color: saved ? '#22c55e' : 'var(--text-main)', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem'
                                    }}
                                >
                                    {saved ? <CheckCircle2 size={15} /> : <Save size={15} />}
                                    {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Draft'}
                                </motion.button>

                                <motion.button
                                    whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                                    onClick={() => submit(true)}
                                    disabled={saving || sending || !studentFeedback.trim()}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: '7px',
                                        padding: '9px 18px', borderRadius: '12px', border: 'none',
                                        background: !studentFeedback.trim() ? 'rgba(34,197,94,0.15)' : 'linear-gradient(135deg,#22c55e,#16a34a)',
                                        color: '#fff', cursor: !studentFeedback.trim() ? 'not-allowed' : 'pointer',
                                        fontWeight: 700, fontSize: '0.88rem', opacity: !studentFeedback.trim() ? 0.5 : 1
                                    }}
                                >
                                    <Send size={15} />
                                    {sending ? 'Sending...' : 'Save & Send to Team'}
                                </motion.button>

                                {!studentFeedback.trim() && (
                                    <span style={{ fontSize: '0.73rem', opacity: 0.5, alignSelf: 'center' }}>
                                        Write Student Feedback to enable Send
                                    </span>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

// ─── Main Review Dashboard ──────────────────────────────────
export default function ReviewDashboard() {
    const { taskId } = useParams();
    const navigate = useNavigate();

    const [task, setTask] = useState(null);
    const [teams, setTeams] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [activeSessionId, setActiveSessionId] = useState(null);

    // teamId → array of ALL reviews (one per reviewer)
    const [reviewMap, setReviewMap] = useState({});

    const [loading, setLoading] = useState(true);
    const [accessDenied, setAccessDenied] = useState(false);
    const [isCoordinator, setIsCoordinator] = useState(false);
    const [showNewSession, setShowNewSession] = useState(false);
    const [newSessionName, setNewSessionName] = useState('');
    const [newSessionDate, setNewSessionDate] = useState(new Date().toISOString().split('T')[0]);
    const [creatingSession, setCreatingSession] = useState(false);
    const [searchFilter, setSearchFilter] = useState('');

    const user = JSON.parse(localStorage.getItem('vjconnect_user') || '{}');

    // Build reviewMap: teamId → array of reviews (all reviewers, kept separate)
    const buildReviewMap = (reviews) => {
        const map = {};
        (reviews || []).forEach(r => {
            if (!map[r.team_id]) map[r.team_id] = [];
            map[r.team_id].push(r);
        });
        setReviewMap(map);
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const taskRes = await fetch(`${API_URL}/formation/tasks?email=${user.email}&role=${user.role || ''}`);
            const taskData = await taskRes.json();
            const found = (taskData.tasks || []).find(t => String(t.id) === taskId);
            setTask(found || null);

            if (found) {
                const ADMIN_EMAILS = ["24071a6691@vnrvjiet.in", "24071a6679@vnrvjiet.in"];
                const isAdmin = ADMIN_EMAILS.includes((user.email || '').toLowerCase().trim());
                const isCreator = found.creator_email?.toLowerCase().trim() === (user.email || '').toLowerCase().trim();
                const isCollab = found.collaborators?.some(c => c.email?.toLowerCase().trim() === (user.email || '').toLowerCase().trim());
                setIsCoordinator(isAdmin || isCreator || isCollab);
            }

            const teamsRes = await fetch(`${API_URL}/formation/tasks/${taskId}/teams`);
            const teamsData = await teamsRes.json();
            setTeams((teamsData.teams || []).sort((a, b) => (a.team_number || 0) - (b.team_number || 0)));

            const sessionsRes = await fetch(`${API_URL}/formation/tasks/${taskId}/review-sessions?email=${user.email}`);
            if (sessionsRes.status === 403) { setAccessDenied(true); setLoading(false); return; }
            const sessionsData = await sessionsRes.json();
            const fetchedSessions = sessionsData.sessions || [];
            setSessions(fetchedSessions);

            if (fetchedSessions.length > 0 && !activeSessionId) {
                setActiveSessionId(fetchedSessions[0].id);
                buildReviewMap(fetchedSessions[0].reviews);
            } else if (activeSessionId) {
                const current = fetchedSessions.find(s => s.id === activeSessionId);
                if (current) buildReviewMap(current.reviews || []);
            }
        } catch (err) {
            console.error('ReviewDashboard fetch error', err);
        } finally {
            setLoading(false);
        }
    }, [taskId, user.email, activeSessionId]);

    useEffect(() => { fetchData(); }, [taskId]);

    const handleSelectSession = (session) => {
        setActiveSessionId(session.id);
        buildReviewMap(session.reviews || []);
    };

    const handleCreateSession = async () => {
        if (!newSessionName.trim()) return;
        setCreatingSession(true);
        try {
            const res = await fetch(`${API_URL}/formation/tasks/${taskId}/review-sessions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    session_name: newSessionName.trim(),
                    reviewer_email: user.email,
                    review_date: newSessionDate
                })
            });
            const data = await res.json();
            if (data.success) {
                setNewSessionName('');
                setShowNewSession(false);
                await fetchData();
                setActiveSessionId(data.sessionId);
                setReviewMap({});
            } else {
                alert(data.error || 'Failed to create session');
            }
        } catch (err) {
            alert('Error creating session');
        } finally {
            setCreatingSession(false);
        }
    };

    const filteredTeams = teams.filter(t =>
        !searchFilter ||
        t.team_name.toLowerCase().includes(searchFilter.toLowerCase()) ||
        String(t.team_number).includes(searchFilter) ||
        (t.members || []).some(m =>
            (m.name || '').toLowerCase().includes(searchFilter.toLowerCase()) ||
            (m.rollNo || '').toLowerCase().includes(searchFilter.toLowerCase())
        )
    );

    if (loading) return <LoadingScreen message="Review Dashboard" />;

    if (accessDenied) return (
        <div className="home-page-container">
            <div className="home-content-wrapper" style={{ textAlign: 'center', paddingTop: '6rem' }}>
                <AlertCircle size={56} style={{ color: '#ef4444', marginBottom: '1rem' }} />
                <h2>Access Denied</h2>
                <p style={{ opacity: 0.6 }}>You don't have reviewer or coordinator access to this room.</p>
                <button onClick={() => navigate('/formation')} className="btn-profile btn-profile-secondary" style={{ marginTop: '1rem' }}>
                    Back to Formation Hub
                </button>
            </div>
        </div>
    );

    if (!task) return (
        <div className="home-page-container">
            <div className="home-content-wrapper" style={{ textAlign: 'center', paddingTop: '6rem' }}>
                <p style={{ opacity: 0.5 }}>Task not found.</p>
            </div>
        </div>
    );

    const activeSession = sessions.find(s => s.id === activeSessionId);

    return (
        <div className="home-page-container">
            <div className="home-content-wrapper" style={{ maxWidth: '1100px' }}>

                {/* Header */}
                <header className="home-header" style={{ marginBottom: '2rem' }}>
                    <div className="brand-section">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{
                                width: 44, height: 44, borderRadius: '14px', flexShrink: 0,
                                background: 'linear-gradient(135deg,#f59e0b,#ef4444)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <ClipboardList size={22} color="white" />
                            </div>
                            <div>
                                <h1 className="brand-title" style={{ fontSize: '1.6rem' }}>Review Dashboard</h1>
                                <p style={{ margin: 0, opacity: 0.55, fontSize: '0.85rem' }}>{task.name}</p>
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                        {isCoordinator && (
                            <button
                                onClick={() => window.open(`${API_URL}/formation/tasks/${taskId}/export-reviews?email=${user.email}`, '_blank')}
                                title="Export all reviews to Excel"
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    padding: '0 16px', height: 46, borderRadius: '14px',
                                    background: 'linear-gradient(135deg,#107c41,#16a34a)', color: '#fff',
                                    border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem'
                                }}
                            >
                                <Download size={16} /> Export Excel
                            </button>
                        )}
                        <button onClick={() => navigate(`/formation/${taskId}`)} title="Back" style={{
                            width: 46, height: 46, borderRadius: '14px',
                            border: '1px solid var(--glass-border)', background: 'var(--glass-bg)',
                            color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}><ChevronLeft size={20} /></button>
                        <button onClick={() => navigate(user?.role === 'faculty' ? '/faculty/home' : '/home')} title="Home" style={{
                            width: 46, height: 46, borderRadius: '14px',
                            border: '1px solid var(--glass-border)', background: 'var(--glass-bg)',
                            color: 'var(--text-main)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}><Home size={20} /></button>
                        <button onClick={() => { localStorage.removeItem('vjconnect_user'); navigate('/'); }} title="Logout" style={{
                            width: 46, height: 46, borderRadius: '14px', border: 'none', background: '#ef4444',
                            color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}><LogOut size={20} /></button>
                    </div>
                </header>

                {/* Sessions Bar */}
                <div className="glass-card" style={{ padding: '1.2rem 1.4rem', marginBottom: '1.8rem', borderRadius: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: sessions.length > 0 ? '1rem' : 0, flexWrap: 'wrap', gap: '10px' }}>
                        <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Calendar size={16} style={{ color: '#6366f1' }} /> Review Sessions
                        </h2>
                        <motion.button
                            whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                            onClick={() => setShowNewSession(s => !s)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px',
                                borderRadius: '12px', border: '1px solid rgba(99,102,241,0.35)',
                                background: showNewSession ? 'rgba(99,102,241,0.15)' : 'transparent',
                                color: '#818cf8', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem'
                            }}
                        >
                            <Plus size={15} /> New Session
                        </motion.button>
                    </div>

                    <AnimatePresence>
                        {showNewSession && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                style={{ overflow: 'hidden', marginBottom: '1rem' }}
                            >
                                <div style={{
                                    background: 'rgba(99,102,241,0.07)', border: '1px solid rgba(99,102,241,0.2)',
                                    borderRadius: '14px', padding: '1rem 1.2rem',
                                    display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end'
                                }}>
                                    <div style={{ flex: 1, minWidth: '200px' }}>
                                        <label style={{ fontSize: '0.75rem', fontWeight: 600, opacity: 0.6, display: 'block', marginBottom: '5px' }}>Session Name</label>
                                        <input
                                            className="profile-input"
                                            style={{ marginBottom: 0 }}
                                            placeholder="e.g. Week 1 PPT Review, Mid-Sem Review..."
                                            value={newSessionName}
                                            onChange={e => setNewSessionName(e.target.value)}
                                            autoFocus
                                            onKeyDown={e => e.key === 'Enter' && handleCreateSession()}
                                        />
                                    </div>
                                    <div>
                                        <label style={{ fontSize: '0.75rem', fontWeight: 600, opacity: 0.6, display: 'block', marginBottom: '5px' }}>Date</label>
                                        <input
                                            type="date"
                                            className="profile-input"
                                            style={{ marginBottom: 0 }}
                                            value={newSessionDate}
                                            onChange={e => setNewSessionDate(e.target.value)}
                                        />
                                    </div>
                                    <button
                                        onClick={handleCreateSession}
                                        disabled={!newSessionName.trim() || creatingSession}
                                        style={{
                                            padding: '10px 20px', borderRadius: '12px', border: 'none',
                                            background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff',
                                            cursor: 'pointer', fontWeight: 700, fontSize: '0.88rem', flexShrink: 0
                                        }}
                                    >
                                        {creatingSession ? 'Creating...' : 'Create'}
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Session Pills */}
                    {sessions.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '1rem', opacity: 0.45, fontSize: '0.9rem' }}>
                            No review sessions yet — click "New Session" to start evaluating teams.
                        </div>
                    ) : (
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {sessions.map(s => {
                                // Count unique reviewers who submitted (not just total rows)
                                const uniqueReviewerCount = new Set((s.reviews || []).map(r => r.reviewer_email)).size;
                                const teamsReviewed = new Set((s.reviews || []).map(r => r.team_id)).size;
                                const isActive = s.id === activeSessionId;
                                return (
                                    <motion.button
                                        key={s.id}
                                        whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                                        onClick={() => handleSelectSession(s)}
                                        style={{
                                            padding: '7px 14px', borderRadius: '12px',
                                            border: `1px solid ${isActive ? 'rgba(99,102,241,0.5)' : 'var(--glass-border)'}`,
                                            background: isActive ? 'linear-gradient(135deg,rgba(99,102,241,0.2),rgba(139,92,246,0.15))' : 'rgba(255,255,255,0.04)',
                                            color: isActive ? '#818cf8' : 'var(--text-main)', cursor: 'pointer',
                                            fontWeight: isActive ? 700 : 500, fontSize: '0.85rem',
                                            display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '1px'
                                        }}
                                    >
                                        <span>{s.session_name}</span>
                                        <span style={{ fontSize: '0.68rem', opacity: 0.55 }}>
                                            {s.review_date} · {uniqueReviewerCount} reviewer{uniqueReviewerCount !== 1 ? 's' : ''} · {teamsReviewed} team{teamsReviewed !== 1 ? 's' : ''}
                                        </span>
                                    </motion.button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Teams */}
                {activeSessionId ? (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem', flexWrap: 'wrap', gap: '10px' }}>
                            <div>
                                <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700 }}>{activeSession?.session_name}</h2>
                                <p style={{ margin: '2px 0 0', fontSize: '0.8rem', opacity: 0.5 }}>
                                    <Clock size={12} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                                    {activeSession?.review_date} · Created by: {activeSession?.reviewer_name || activeSession?.reviewer_email}
                                </p>
                            </div>
                            <input
                                className="profile-input"
                                style={{ maxWidth: '240px', marginBottom: 0, padding: '8px 14px' }}
                                placeholder="🔍  Filter teams..."
                                value={searchFilter}
                                onChange={e => setSearchFilter(e.target.value)}
                            />
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            {filteredTeams.length === 0 ? (
                                <div className="glass-card" style={{ padding: '3rem', textAlign: 'center', opacity: 0.5 }}>
                                    <Users size={40} style={{ marginBottom: '1rem' }} />
                                    <p>No teams found.</p>
                                </div>
                            ) : (
                                filteredTeams.map(team => {
                                    const allTeamReviews = reviewMap[team.id] || [];
                                    // This reviewer's own previous submission
                                    const myReview = allTeamReviews.find(r =>
                                        r.reviewer_email?.toLowerCase() === (user.email || '').toLowerCase()
                                    ) || null;

                                    return (
                                        <TeamCard
                                            key={team.id}
                                            team={team}
                                            task={task}
                                            sessionId={activeSessionId}
                                            reviewerEmail={user.email}
                                            myReview={myReview}
                                            allReviews={isCoordinator ? allTeamReviews : null}
                                            onSaved={fetchData}
                                            isCoordinator={isCoordinator}
                                        />
                                    );
                                })
                            )}
                        </div>
                    </>
                ) : (
                    sessions.length > 0 ? (
                        <div className="glass-card" style={{ padding: '3rem', textAlign: 'center', opacity: 0.5 }}>
                            Select a session above to start evaluating.
                        </div>
                    ) : null
                )}
            </div>
        </div>
    );
}
